// frontend/src/services/imagingUploadService.js
// Service for automated imaging PDF upload and transcription

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

/**
 * Upload and process imaging PDF with AI extraction
 * @param {string} patientId - Patient ID
 * @param {File} pdfFile - PDF file to upload
 * @param {string} studyDate - Study date (optional, YYYY-MM-DD format)
 * @returns {Promise} - Extracted imaging data for review
 */
export const processImagingPdf = async (patientId, pdfFile, studyDate = null) => {
  try {
    const formData = new FormData();
    formData.append('imagingPdf', pdfFile);
    if (studyDate) {
      formData.append('studyDate', studyDate);
    }

    const response = await axios.post(
      `${API_BASE_URL}/patients/${patientId}/imaging-upload/process`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minute timeout for AI processing
      }
    );

    return response.data;
  } catch (error) {
    console.error('[ImagingUploadService] Error processing imaging PDF:', error);
    throw error;
  }
};

/**
 * Import reviewed imaging data to flowsheet
 * @param {string} patientId - Patient ID
 * @param {Object} reviewedData - Reviewed and edited imaging data
 * @param {string} confirmedStudyDate - Confirmed study date (YYYY-MM-DD format)
 * @param {Object} tempFileInfo - Temporary file information from processing step
 * @param {string} documentName - Name for the saved document
 * @param {string} documentDescription - Description for the saved document
 * @returns {Promise} - Import result
 */
export const importReviewedImagingData = async (
  patientId,
  reviewedData,
  confirmedStudyDate,
  tempFileInfo,
  documentName,
  documentDescription
) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/patients/${patientId}/imaging-upload/import`,
      {
        reviewedData,
        confirmedStudyDate,
        tempFileInfo,
        documentName,
        documentDescription,
      },
      {
        timeout: 60000, // 1 minute timeout
      }
    );

    return response.data;
  } catch (error) {
    console.error('[ImagingUploadService] Error importing imaging data:', error);
    throw error;
  }
};

/**
 * Validate PDF file before upload
 * @param {File} file - File to validate
 * @returns {Object} - Validation result with isValid and error message
 */
export const validateImagingPdf = (file) => {
  const maxSize = 100 * 1024 * 1024; // 100MB
  const allowedType = 'application/pdf';

  if (!file) {
    return {
      isValid: false,
      error: 'No file selected',
    };
  }

  if (file.type !== allowedType) {
    return {
      isValid: false,
      error: 'Only PDF files are allowed',
    };
  }

  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'File size must be less than 100MB',
    };
  }

  return {
    isValid: true,
    error: null,
  };
};

/**
 * Format extracted data for display in review modal
 * @param {Object} extractedData - Raw extracted data from AI
 * @returns {Object} - Formatted data for display
 */
export const formatExtractedDataForReview = (extractedData) => {
  return {
    category: extractedData.category || 'Other Imaging',
    studyType: extractedData.studyType || 'Unknown Study',
    studyDate: extractedData.studyDate || null,
    modality: extractedData.modality || 'Unknown',
    bodyRegion: extractedData.bodyRegion || '',
    findings: extractedData.findings || [],
    measurements: extractedData.measurements || '',
    interpretation: extractedData.interpretation || '',
    impression: extractedData.impression || '',
    recommendations: extractedData.recommendations || '',
    comparison: extractedData.comparison || '',
    technique: extractedData.technique || '',
    contrast: extractedData.contrast || '',
  };
};

/**
 * Get imaging category display name
 * @param {string} category - Category key
 * @returns {string} - Display name
 */
export const getImagingCategoryDisplayName = (category) => {
  const categoryMap = {
    'Chest XR': 'Chest X-Ray',
    'EKG': 'Electrocardiogram (EKG)',
    'Heart US': 'Heart Ultrasound',
    'Carotid US': 'Carotid Ultrasound',
    'Liver US': 'Liver Ultrasound',
    'Spleen US': 'Spleen Ultrasound',
    'Bladder US': 'Bladder Ultrasound',
    'Kidney US': 'Kidney Ultrasound',
    'Thyroid US': 'Thyroid Ultrasound',
    'Gallbladder US': 'Gallbladder Ultrasound',
    'Abdominal US': 'Abdominal Ultrasound',
    'Other Imaging': 'Other Imaging Study',
  };

  return categoryMap[category] || category;
};

/**
 * Get confidence level display
 * @param {number} confidence - Confidence score (0-1)
 * @returns {Object} - Display info with text and color
 */
export const getConfidenceDisplay = (confidence) => {
  if (confidence >= 0.9) {
    return { text: 'High', color: 'success' };
  } else if (confidence >= 0.7) {
    return { text: 'Medium', color: 'warning' };
  } else {
    return { text: 'Low', color: 'error' };
  }
};

/**
 * Prepare data for import (validate and structure)
 * @param {Object} reviewedData - Data reviewed by user
 * @param {string} studyDate - Confirmed study date
 * @returns {Object} - Structured data ready for import
 */
export const prepareDataForImport = (reviewedData, studyDate) => {
  // Filter out findings with empty values
  const validFindings = (reviewedData.findings || []).filter(
    (finding) => finding.value && finding.value.trim() !== ''
  );

  return {
    ...reviewedData,
    findings: validFindings,
    studyDate: studyDate,
  };
};

/**
 * Get default document name based on study info
 * @param {Object} extractedData - Extracted imaging data
 * @param {string} studyDate - Study date
 * @returns {string} - Default document name
 */
export const getDefaultDocumentName = (extractedData, studyDate) => {
  const studyType = extractedData.studyType || 'Imaging Study';
  const dateStr = studyDate || new Date().toISOString().split('T')[0];
  return `${studyType} - ${dateStr}`;
};

/**
 * Get default document description
 * @param {Object} extractedData - Extracted imaging data
 * @returns {string} - Default document description
 */
export const getDefaultDocumentDescription = (extractedData) => {
  const studyType = extractedData.studyType || 'Unknown study';
  const modality = extractedData.modality || 'Unknown modality';
  return `AI-extracted imaging report: ${studyType} (${modality})`;
};

export default {
  processImagingPdf,
  importReviewedImagingData,
  validateImagingPdf,
  formatExtractedDataForReview,
  getImagingCategoryDisplayName,
  getConfidenceDisplay,
  prepareDataForImport,
  getDefaultDocumentName,
  getDefaultDocumentDescription,
};

